import React, { useState, useEffect } from 'react'
import { useStoreState } from '../../model/hooks'
import { useWalletContext } from '../WalletContext'
import Popup from '../Popup'
import PrimaryButton from '../PrimaryButton'
import { fetchPoolPrice } from '../../model/clpool/helper'
import { Network } from '../../services/token.service'
import { DEFAULT_SLIPPAGE } from '../BoxHeader/BoxHeader'
import { RebalanceSimulationResult, rebalancePosition } from '../../services/position'
import { useStoreActions } from '../../model/hooks'
import { Token, Transaction } from '../../atoms'
// import { HelptipIcon } from '../../svgIcons/HelptipIcon'
import { calculateRatioFromTickForTokens, formatFloatingPointNumber, formatNumber, formatUSD, normalizeForToken } from '../../blockchainUtils/math'
import { GAtrack } from '../../services/ga'
import { getPriceFromState } from '../../model/pricing/helper'
import { ArrowDownRebalanceIcon } from '../../svgIcons/ArrowDownRebalanceIcon'
import BigNumber from 'bignumber.js'
import axios from 'axios'
import { apiUrl } from '../../blockchainUtils'
import Spinner from '../Spinner'
// import { HelptipIcon } from '../../svgIcons/HelptipIcon'
import { XIcon } from '../../svgIcons/XIcon'
import { MAX_TICK } from '../PoolsPage/helper'

function RebalancePopup(props: { positionIndex: number; isOpen: boolean; onClose: () => void }) {
    const { positionIndex, isOpen, onClose } = props
    const { walletAddress: connectedAddress, networkName: network } = useWalletContext()
    const clPosition = useStoreState((state) => state.clPools.getCLPositionByIndex(network!, positionIndex))
    const selectedPool = useStoreState((state) => state.clPools.getPoolByAddress(network!, clPosition?.poolAddress ?? ''))
    const [isRebalancing, setIsRebalancing] = useState(false)
    const [isFetchingData, setIsFetchingData] = useState(false)
    const [slippage, setSlippage] = useState(Number(DEFAULT_SLIPPAGE))
    const [priceImpact, setPriceImpact] = useState<string>()
    const loadTransaction = useStoreActions((actions) => actions.transaction.loadTransaction)
    const [txHash, setTxHash] = useState('')
    const [rangeOfPositionInPercent, setRangeOfPositionInPercent] = useState<number>()
    const [canRebalance, setCanRebalance] = useState<boolean>()
    const [impactColor, setImpactColor] = useState('white')
    const [rebalanceResult, setRebalanceResult] = useState({} as RebalanceSimulationResult)
    const token1 = useStoreState((state) => state.tokens.getToken(network || '', clPosition ? clPosition.token0 : ''))
    const token2 = useStoreState((state) => state.tokens.getToken(network || '', clPosition ? clPosition.token1 : ''))
    const transaction = useStoreState((state) => state.transaction.getTransaction(txHash))
    const loadPositons = useStoreActions((actions) => actions.clPools.loadPositons)
    const loadBalances = useStoreActions((actions) => actions.tokens.loadBalances)
    const [isReversed, sedIsReversed] = useState(false)
    const [projectedApr, setProjectedApr] = useState(undefined as unknown as number)

    useEffect(() => {
        if (rebalanceResult && rebalanceResult.tick_lower) loadProjectedAPR()
    }, [rebalanceResult])

    // A/B test stuff
    // Function to determine A/B variation based on last character of connectedAddress
    function getVariation(): string {
        if (connectedAddress) {
            const lastChar = connectedAddress.slice(-1).toLowerCase()
            return parseInt(lastChar, 16) < 8 ? 'A' : 'B'
        }

        return 'A'
    }

    // Function to create event name with variation suffix
    function createEventName(name: string): string {
        const variation = getVariation()
        return `${name}_${variation}`
    }

    function sendGAEvent(name: string) {
        const ename = createEventName(name)
        GAtrack(ename, ename, ename)
    }

    async function loadProjectedAPR() {
        if (!clPosition || !rebalanceResult || !rebalanceResult.tick_lower) return
        const { data } = await axios.get(
            `${apiUrl(network ?? '')}/cl/apr?pkey=${clPosition?.poolAddress}&range_low=${rebalanceResult.tick_lower}&range_high=${rebalanceResult.tick_upper}`
        )

        const apr = data.apr ? data.apr : 0
        setProjectedApr(apr * 100)
    }

    const fetchData = async () => {
        if (isRebalancing) return
        // Add your fetch logic here
        setIsFetchingData(true)
        if (selectedPool && clPosition) {
            await fetchPoolPrice(network! as Network, selectedPool)

            // simulate the rebalance
            const response = await rebalancePosition(network! as Network, clPosition, selectedPool.currentPrice!, slippage, true, connectedAddress!)
            const rebalance_result = response as RebalanceSimulationResult
            setRebalanceResult(rebalance_result)
            console.log('YSYS Simulation of rebalance result=', rebalance_result)
            const pimpact = rebalance_result ? rebalance_result.priceImpact : 0
            setPriceImpact(pimpact || 'N/A')
            const rangeInPercent = Math.round((Math.exp((clPosition.tickUpper - clPosition.tickLower) * Math.log(1.0001)) - 1.0) * 1000) / 10 // 1 decimal point
            console.log('YSYS rangeInPercent=', rangeInPercent)
            setRangeOfPositionInPercent(rangeInPercent)

            if (pimpact && rangeInPercent) {
                const impact_from_range = Number(pimpact) / (rangeInPercent / 2)
                if (impact_from_range > 0.99) {
                    setImpactColor('#FF4D4D')
                    setCanRebalance(false)
                } else if (impact_from_range > 0.5) {
                    setImpactColor('#FFBC6C')
                    setCanRebalance(true)
                } else {
                    setImpactColor('white')
                    setCanRebalance(true)
                }
            }
            setIsFetchingData(false)
        }
    }

    useEffect(() => {
        if (transaction && (transaction.status === 'ACCEPTED_ON_L2' || transaction.status === 'ACCEPTED_ON_L1')) {
            setIsRebalancing(false)
            loadBalances({ network: network as Network, walletAddress: connectedAddress! })
            loadPositons({ network: network as Network, address: connectedAddress!, selectedPosition: positionIndex })
            setTxHash('')
            onClose()
        }
        if (transaction && transaction.status === 'REVERTED') {
            setIsRebalancing(false)
            setTxHash('')
        }
    }, [transaction])

    useEffect(() => {
        if (isOpen && !isRebalancing) {
            fetchData()
            if (network === 'mainnet-alpha') sendGAEvent('rebalance_popup_opened')
        }
    }, [isOpen])

    async function onRebalanceClick() {
        if (isFetchingData) return
        if (isRebalancing) return
        if (!clPosition) return
        if (!selectedPool) return
        if (!selectedPool.currentPrice) return

        setIsRebalancing(true)

        try {
            if (network === 'mainnet-alpha') sendGAEvent('rebalance_clicked')

            const response = await rebalancePosition(network! as Network, clPosition, selectedPool?.currentPrice, slippage, false, connectedAddress!)
            const tx = (response as { success: boolean; error?: string; tx: Transaction | undefined })!.tx
            if (tx) {
                if (network === 'mainnet-alpha') sendGAEvent('rebalance_tx_sent')
                const timestampSent = new Date().getTime()
                loadTransaction({ txPayload: { [tx.hash]: { ...tx, labelFields: { timestampSent: timestampSent } } }, isGoogleLog: network === 'mainnet-alpha' })
                setTxHash(tx.hash)
            } else {
                if (network === 'mainnet-alpha') sendGAEvent('rebalance_tx_declined')

                setIsRebalancing(false)
            }
        } catch (e: any) {
            setIsRebalancing(false)
        }
    }

    function renderTokensLine(token1: Token | null, token2: Token | null, token1Amount: number, token2Amount: number) {
        if (!token1 || !token2) return null
        const token1Price = getPriceFromState(token1.symbol.toLowerCase()) ? getPriceFromState(token1.symbol.toLowerCase())!.usd : 0
        const token2Price = getPriceFromState(token2.symbol.toLowerCase()) ? getPriceFromState(token2.symbol.toLowerCase())!.usd : 0

        return (
            <div className='flex lg:flex-row flex-col lg:items-center'>
                <span className='inline-flex items-center'>
                    <img className='w-[20px] h-[20px] mr-1' src={token1.icon} />
                    <span className='mr-1'>{`${formatFloatingPointNumber(token1Amount.toString())} ${token1.symbol}`}</span>
                    <span className='text-[#8387A5] text-[12px]'>{`($${formatUSD(token1Price * token1Amount)})`}</span>
                    <span className='text-[18px] text-white mx-1'>{'/'}</span>
                </span>
                <span className='inline-flex items-center'>
                    <img className='w-[20px] h-[20px] mr-1' src={token2.icon} />
                    <span className='mr-1'>{`${formatFloatingPointNumber(token2Amount.toString())} ${token2.symbol}`}</span>
                    <span className='text-[#8387A5] text-[12px]'>{`($${formatUSD(token2Amount * token2Price)})`}</span>
                </span>
            </div>
        )
    }

    function renderCurrentPositionData() {
        const token1ValueBn = BigNumber(clPosition?.value && clPosition?.value.token1Amount ? clPosition?.value.token1Amount : 0)
        const token2ValueBn = BigNumber(clPosition?.value && clPosition?.value.token2Amount ? clPosition?.value.token2Amount : 0)
        const token1FeesBn = BigNumber(clPosition?.fees?.token1Amount! ?? 0)
        const token2FeesBn = BigNumber(clPosition?.fees?.token2Amount! ?? 0)
        let token1Value = token1ValueBn.minus(token1FeesBn)
        let token2Value = token2ValueBn.minus(token2FeesBn)

        return (
            <div className='border-[1px] border-[#0C3F6D] px-6 py-4 rounded-[8px]'>
                <div className='flex flex-col'>
                    <span className='text-[#8387A5] text-[12px] font-[400] mb-2'>Current Position</span>
                    {renderTokensLine(token1, token2, token1Value.toNumber(), token2Value.toNumber())}
                    <span className='mt-[12px] text-[#8387A5] text-[12px] font-[400] mb-2'>Current Fees</span>
                    {renderTokensLine(token1, token2, Number(clPosition?.fees?.token1Amount || 0), Number(clPosition?.fees?.token2Amount || 0))}
                </div>
            </div>
        )
    }

    function renderRangeSelector() {
        return (
            <div className='bg-[#0C2960] shadow-xl rounded-[8px] p-1 '>
                <span onClick={() => sedIsReversed(true)} className={`cursor-pointer p-1 rounded-[8px] ${isReversed ? 'bg-[#0AE8F9] text-[#0D0C1E]' : ''}`}>
                    {token1?.symbol}
                </span>
                <span onClick={() => sedIsReversed(false)} className={`cursor-pointer p-1 rounded-[8px] ${!isReversed ? 'bg-[#0AE8F9] text-[#0D0C1E]' : ''}`}>
                    {token2?.symbol}
                </span>
            </div>
        )
    }

    function renderRebalancedTo() {
        if (!token1 || !token2) return null
        // if (!rebalanceResult || !token1 || !token2 || !rebalanceResult.tick_lower) return null

        let minPrice = rebalanceResult && rebalanceResult.tick_lower ? calculateRatioFromTickForTokens(rebalanceResult.tick_lower - MAX_TICK, token1, token2) : 0
        let maxPrice = rebalanceResult && rebalanceResult.tick_upper ? calculateRatioFromTickForTokens(rebalanceResult.tick_upper - MAX_TICK, token1, token2) : 0
        if (isReversed) {
            const intermediateMax = maxPrice
            const intermediateMin = minPrice
            minPrice = BigNumber(1).div(intermediateMax)
            maxPrice = BigNumber(1).div(intermediateMin)
        }
        const tokenObj1 = isReversed ? token2 : token1
        const tokenObj2 = isReversed ? token1 : token2
        return (
            <div className='border-[1px] border-[#0C3F6D] px-6 py-4 rounded-[8px] w-full'>
                <div className='flex flex-col w-full'>
                    <div className='flex items-center justify-between'>
                        <span className='text-[18px] text-white font-bold mb-[10px]'>Will be rebalanced to:</span>
                        {renderRangeSelector()}
                    </div>
                    <span className='text-[#8387A5] text-[12px] font-[400] mb-2'>Estimated range</span>
                    <span className='inline w-full'>
                        <span className='text-white'>{formatFloatingPointNumber(minPrice.toString())}</span>
                        <span className='mx-2 text-[#8387A5]'>{'to'}</span>
                        <span className='text-white'>{formatFloatingPointNumber(maxPrice.toString())}</span>
                        <img className='inline w-[20px] h-[20px] mx-2' src={tokenObj2 ? tokenObj2.icon : ''} />
                        <span className='text-white'>{tokenObj2.symbol}</span>
                        <span className='ml-2 text-[#8387A5] mr-2'>{'per'}</span>
                        <img className='inline w-[20px] h-[20px] mr-2' src={tokenObj1 ? tokenObj1.icon : ''} />
                        <span className='text-white'>{tokenObj1.symbol}</span>
                    </span>
                    <span className='text-[#8387A5] mt-3 text-[12px] font-[400] mb-2'>Estimated new position </span>
                    {renderTokensLine(
                        token1,
                        token2,
                        normalizeForToken(rebalanceResult && rebalanceResult.amount0 ? rebalanceResult.amount0 : BigNumber(0), token1).toNumber(),
                        normalizeForToken(rebalanceResult && rebalanceResult.amount1 ? rebalanceResult.amount1 : BigNumber(0), token2).toNumber()
                    )}
                    <div className='flex lg:flex-row flex-col lg:items-center justify-between mt-4'>
                        <div className='flex'>
                            <div className='bg-[#0C3F6D] flex items-center py-1 px-2 rounded-[8px] flex-shrink-0 mr-2'>
                                <span className='font-bold mr-1 text-[12px]' style={{ color: 'rgba(255, 255, 255, 0.50)' }}>
                                    Estimated APR:
                                </span>
                                {projectedApr ? <span className='text-[#0AE8F9] font-bold'>{formatNumber(projectedApr) + '%'}</span> : null}
                                {projectedApr ? null : <Spinner />}
                            </div>
                        </div>
                        <div className='flex items-center mt-2'>
                            <span className='text-[14px] text-white'>Leftovers go back to your wallet</span>
                            {/* <HelptipIcon className='flex-shrink-0 w-[16px] h-[16px] ml-2' /> */}
                        </div>
                    </div>
                    <span className='mt-4 text-[14px]'>Fees will be added to your new position, and your leaderboard points will remain.</span>
                </div>
            </div>
        )
    }

    const isA = getVariation() == 'A'

    return (
        <Popup contentClassName='max-w-[528px] overflow-auto lg:w-[528px] w-full' open={isOpen} onClose={onClose}>
            <div className='flex h-[100vh] lg:h-auto flex-col '>
                {/* {renderHeader()} */}
                <div className='flex items-center justify-between mb-6'>
                    <span className='text-[24px] font-bold text-white'>Rebalance</span>
                    <XIcon onClick={onClose} className='cursor-pointer w-4 h-4' />
                </div>
                {isA && (
                    <>
                        {token1 && token2 ? (
                            <div className='mb-6 relative flex items-center'>
                                <img className='w-[36px] h-[36px]' src={token1 ? token1.icon : ''} />
                                <img className='w-[36px] relative left-[-14px] h-[36px]' src={token2 ? token2.icon : ''} />
                                <span className='text-[18px] font-bold text-white'>{`${token1.symbol} / ${token2.symbol}`}</span>
                                {clPosition ? (
                                    <div className='bg-[#0C3F6D] p-1 rounded-[8px] text-[#0AE8F9] text-[16px] text-center ml-2'>{`${clPosition.fee / 10000}%`}</div>
                                ) : null}
                            </div>
                        ) : null}
                        <div className='relative w-full'>
                            {renderCurrentPositionData()}
                            <div className='flex w-full justify-center mt-[-8px] mb-[-8px]'>
                                <div className='h-[36px] w-[36px] rounded-full bg-[#0C1647] border-[2px] border-[#0C3F6D] flex items-center justify-center'>
                                    <ArrowDownRebalanceIcon className='w-[20px] h-[20px]' />
                                </div>
                            </div>
                            {renderRebalancedTo()}
                        </div>
                    </>
                )}
                {!isA && (
                    <>
                        <div className='mt-[40px]'>
                            <span className='text-white text-[18px]'>
                                We will rebalance your position by burning your current position, swapping and minting a new one around the current price so you will continue to
                                accrue fees. Current fees will go into the new position - they are not gone! Your leaderboard points remain as well.
                            </span>
                        </div>
                    </>
                )}
                <div className='flex items-center justify-between mt-6'>
                    <span className='text-[16px] text-white font-[600]'>Price impact</span>
                    {rebalanceResult && rebalanceResult.priceImpact ? <span className='text-[18px] font-[600]'>{`${rebalanceResult.priceImpact}%`}</span> : <Spinner />}
                </div>

                <PrimaryButton isDisabled={isFetchingData || !canRebalance} onClick={onRebalanceClick} isLoading={isRebalancing} className='mt-[46px]' size='lg'>
                    <span className='text-[18px]'>{canRebalance === false ? 'Cannot Rebalance' : 'Rebalance'}</span>
                </PrimaryButton>
            </div>
        </Popup>
    )
}

export default RebalancePopup
